import * as React from "react";
import {
    Button,
    useId,
    Toaster,
    useToastController,
    Toast,
    ToastTitle,
    ToastBody,
    ToastFooter,
} from "@fluentui/react-components";

const ToastContext = React.createContext<{ showToast: (options: ToastOptions) => void } | null>(null);

type ToastOptions = {
    title: string;
    body?: React.ReactNode;
    previewBody?: React.ReactNode;
    footerActions?: React.ReactNode;
    intent?: "success" | "error" | "warning" | "info";
    timeoutMs?: number;
    persistent?: boolean;
    dismissible?: boolean;
    showMoreLabel?: string;
    showLessLabel?: string;
    closeLabel?: string;
};

const DEFAULT_TOAST_TIMEOUT_MS = 5000;

const toastFooterActionsStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
};

const toastBodyContentStyle: React.CSSProperties = {
    paddingRight: 4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
};

type ToastContentProps = Pick<ToastOptions, "title" | "body" | "previewBody" | "footerActions" | "dismissible" | "showMoreLabel" | "showLessLabel" | "closeLabel"> & {
    onClose: () => void;
};

const ToastContent: React.FC<ToastContentProps> = ({
    title,
    body,
    previewBody,
    footerActions,
    dismissible = true,
    showMoreLabel = "Show more",
    showLessLabel = "Show less",
    closeLabel = "Close",
    onClose,
}) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const hasExpandableBody = previewBody !== undefined && body !== undefined;
    const visibleBody = hasExpandableBody ? (isExpanded ? body : previewBody) : body ?? previewBody;
    const hasFooter = hasExpandableBody || footerActions;

    return (
        <Toast>
            <ToastTitle
                action={
                    dismissible ? (
                        <Button appearance="subtle" size="small" onClick={onClose}>
                            {closeLabel}
                        </Button>
                    ) : undefined
                }
            >
                {title}
            </ToastTitle>
            {visibleBody && (
                <ToastBody>
                    <div style={toastBodyContentStyle}>{visibleBody}</div>
                </ToastBody>
            )}
            {hasFooter && (
                <ToastFooter>
                    <div style={toastFooterActionsStyle}>
                        {hasExpandableBody && (
                            <Button appearance="transparent" size="small" onClick={() => setIsExpanded((current) => !current)}>
                                {isExpanded ? showLessLabel : showMoreLabel}
                            </Button>
                        )}
                        {footerActions}
                    </div>
                </ToastFooter>
            )}
        </Toast>
    );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const toasterId = useId("toaster");
    const toastIdRef = React.useRef(0);
    const { dispatchToast, dismissToast } = useToastController(toasterId);

    const showToast = ({
        title,
        body,
        previewBody,
        footerActions,
        intent = "info",
        timeoutMs = DEFAULT_TOAST_TIMEOUT_MS,
        persistent = false,
        dismissible = true,
        showMoreLabel,
        showLessLabel,
        closeLabel,
    }: ToastOptions) => {
        const toastId = `${toasterId}-${toastIdRef.current++}`;

        dispatchToast(
            <ToastContent
                title={title}
                body={body}
                previewBody={previewBody}
                footerActions={footerActions}
                dismissible={dismissible}
                showMoreLabel={showMoreLabel}
                showLessLabel={showLessLabel}
                closeLabel={closeLabel}
                onClose={() => dismissToast(toastId)}
            />,
            { intent, timeout: persistent ? -1 : timeoutMs, toastId }
        );
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            <Toaster toasterId={toasterId} className="mr-2" position="bottom-end" />
            {children}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};
